import React, { useEffect, useState } from 'react'
import { Autocomplete, TextField } from '@mui/material'
import { useHistory, useParams } from 'react-router-dom'
import { AxiosWithAuth } from '../../Utilities/authenticationService'
import { useStickyState } from '../../Utilities/useStickyState'

export const VIEW_ONLY_STORE_KEY = 'selectedStoreViewOnly'

export const readSelectedStoreViewOnly = () => {
    try {
        const raw = window.localStorage.getItem(VIEW_ONLY_STORE_KEY)
        if (raw == null || raw === '') return null
        return JSON.parse(raw)
    } catch {
        return null
    }
}

/**
 * Store picker for 正社員(間接) / view_only users.
 * Loads company stores with sample_employment_id and navigates when the selection changes.
 *
 * Pass selectedStore + onSelectedStoreChange to control selection from a parent
 * (e.g. ViewOnlyHome). Otherwise selection is kept in sticky localStorage.
 */
const ViewOnlyStoreSelect = ({
    sx,
    fullWidth = true,
    navigateOnChange = true,
    selectedStore: controlledStore,
    onSelectedStoreChange
}) => {
    const isControlled = typeof onSelectedStoreChange === 'function'
    const [uncontrolledStore, setUncontrolledStore] = useStickyState(null, VIEW_ONLY_STORE_KEY)
    const selectedStore = isControlled ? controlledStore : uncontrolledStore
    const setSelectedStore = isControlled ? onSelectedStoreChange : setUncontrolledStore

    const [storeOptions, setStoreOptions] = useState([])
    const [loadError, setLoadError] = useState(false)
    const history = useHistory()
    const { employmentId } = useParams()

    useEffect(() => {
        let cancelled = false
        AxiosWithAuth.get('/stores')
            .then(res => {
                if (cancelled) return
                const mapped = (res.data || [])
                    .filter(store => store?.sample_employment_id)
                    .map(store => ({
                        ...store,
                        label: store.name,
                        value: store.seg_code
                    }))
                setStoreOptions(mapped)
                setLoadError(false)
            })
            .catch(() => {
                if (cancelled) return
                setStoreOptions([])
                setLoadError(true)
            })
        return () => {
            cancelled = true
        }
    }, [])

    const handleChange = (event, newValue) => {
        if (!newValue) return
        setSelectedStore(newValue)
        if (!navigateOnChange || !history || !newValue.sample_employment_id) return

        const nextEmpId = String(newValue.sample_employment_id)
        const currentPath = history.location.pathname
        if (employmentId && currentPath.includes(`/${employmentId}/`)) {
            history.push(currentPath.replace(`/${employmentId}/`, `/${nextEmpId}/`))
        }
    }

    return (
        <Autocomplete
            data-testid="view-only-store-select"
            sx={sx}
            className="shrinkable"
            options={storeOptions}
            value={selectedStore}
            getOptionLabel={option =>
                option?.seg_code != null ? `${option.seg_code} - ${option.name}` : ''
            }
            isOptionEqualToValue={(option, value) => option?.seg_code === value?.seg_code}
            autoComplete
            onChange={handleChange}
            onInputChange={(_, __, reason) => {
                if (reason === 'input' || reason === 'clear') {
                    setSelectedStore(null)
                }
            }}
            noOptionsText={loadError ? '店舗一覧の取得に失敗しました' : '店舗がありません'}
            renderInput={params => (
                <TextField
                    {...params}
                    label="店舗名"
                    placeholder="選択してください"
                    InputLabelProps={{ shrink: true }}
                    fullWidth={fullWidth}
                    error={loadError}
                    helperText={loadError ? 'ページを再読み込みして再度お試しください' : undefined}
                />
            )}
        />
    )
}

export default ViewOnlyStoreSelect
